import React, { useRef } from 'react';
import classNames from 'classnames';
import { ImageAvatar } from '@components/avatar/ImageAvatar';
import './DropdownItem.scss';
import { DropdownIconWrapper } from '@components/dropdown/DropdownIconWrapper';
import { Icon, IconName } from '@components/icon';

export interface DropdownItemProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  /** Text of the Dropdown Item */
  label: string;
  /** Image for ImageAvatar component */
  src?: string;
  /** Alt tag for ImageAvatar component */
  alt?: string;
  /** Icon name for DropdownIconWrapper component */
  icon?: IconName;
  /** If true, the chevron is displayed */
  chevron?: boolean;
  /** If true, the top divider is added */
  topDivider?: boolean;
  /** If true, the bottom divider is added */
  bottomDivider?: boolean;
  /** Size of layout for desktop or mobile. Default large */
  size?: 'large' | 'small';
  /** Optional anchor click event callback */
  onAnchorClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Key navigation, 0 by default */
  tabIndex?: number;
  /** Define html element, list item (li) by default */
  component?: React.ElementType;
  /** URL for the anchor tag */
  href?: string;
  /** Target for the anchor tag */
  target?: '_blank' | '_self' | '_parent' | '_top' | '_unfencedTop';
  /** Boolean in case the item is selected (used by SelectDropdown) */
  selected?: boolean;
  /** If true, the item is visually highlighted as the active/focused option during keyboard navigation */
  isActive?: boolean;
  /** If true will add a max-width to the label (for profile dropdown) default true */
  isTruncated?: boolean;
  className?: string;
  linkComponent?: React.ElementType;
}

/**
 * DropdownItem Component
 *
 * Extends `React.HTMLAttributes<HTMLElement>`, so standard handlers like `onClick`,
 * `onMouseOver`, and `onFocus` can be passed directly.
 *
 * @param children
 * @param label - Text of the Dropdown Item
 * @param src - Image for ImageAvatar component
 * @param alt - Alt tag for ImageAvatar component
 * @param icon - Icon for DropdownIconWrapper component
 * @param chevron - If true, the chevron is displayed
 * @param topDivider - if true, the top divider is added
 * @param bottomDivider - if true, the bottom divider is added
 * @param size - size of layout for desktop or mobile. Default large
 * @param tabIndex - key navigation, 0 by default
 * @param component - define html element, list item (li) by default
 * @param href - URL for the anchor tag
 * @param target - Target for the anchor tag
 * @param selected - Boolean in case the item is selected (used by SelectDropdown)
 * @param isActive - If true, the item is visually highlighted as the active/focused option during keyboard navigation
 * @param isTruncated - If true will add a max-width to the label (for profile dropdown) default true
 * @param className - additional class names
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link).
 * @constructor
 */
export const DropdownItem: React.FC<DropdownItemProps> = ({
  children,
  label,
  src,
  alt = 'Profile Image',
  icon,
  chevron = false,
  topDivider = false,
  bottomDivider = true,
  size = 'large',
  onClick,
  onMouseOver,
  onFocus,
  onAnchorClick,
  tabIndex = 0,
  component: Component = 'li',
  href,
  target,
  selected,
  isActive,
  className,
  isTruncated = true,
  linkComponent: LinkWrapper = 'a',
  ...rest
}) => {
  const ref = useRef<HTMLElement | null>(null);

  // Default role is 'button' for general menu/action usage.
  // Consumers rendering within a listbox (e.g. SelectDropdown) should pass role='option'.
  const { role = 'button', ...restProps } = rest;
  const linkRole = role === 'button' ? undefined : role;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      ref.current?.click();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const fromAnchor = !!(e.target as HTMLElement)?.closest?.('a[href]');
    if (fromAnchor) return;

    if (href) {
      // Avatar, icon, children and chevron sit outside the anchor; clicking them must still navigate
      e.currentTarget.querySelector<HTMLElement>('a[href]')?.click();
      return;
    }

    e.preventDefault();
    if (onClick) onClick(e);
  };

  const itemInteractiveProps = href
    ? { role: linkRole ? 'none' : undefined }
    : {
        onKeyDown: handleKeyDown,
        tabIndex,
        role,
        'aria-selected': role === 'option' ? !!selected : undefined,
      };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();

    if (onAnchorClick) onAnchorClick(e);
    if (onClick) onClick(e);
  };

  const handleAnchorKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const mouseOverhandler = (e: React.MouseEvent<HTMLElement>) => {
    if (onMouseOver) {
      onMouseOver(e);
    }
  };

  const focusHandler = (e: React.FocusEvent<HTMLElement>) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  return (
    <Component
      className={classNames(
        'vds-dropdown-item',
        `vds-dropdown-item--${size}`,
        'vds-typography-body-02-regular-small',
        {
          'vds-dropdown-item--top-divider': topDivider,
          'vds-dropdown-item--bottom-divider': bottomDivider,
          'vds-dropdown-item--selected': selected,
          'vds-dropdown-item--active': isActive,
        },
        className,
      )}
      onClick={handleClick}
      onMouseOver={mouseOverhandler}
      onFocus={focusHandler}
      ref={ref}
      {...itemInteractiveProps}
      {...restProps}>
      {src && <ImageAvatar alt={alt} className="vds-dropdown-item__avatar" size="4xs" src={src} />}
      {icon && !src && <DropdownIconWrapper name={icon} />}
      {href ? (
        <LinkWrapper
          onClick={handleAnchorClick}
          onKeyDown={handleAnchorKeyDown}
          href={href}
          target={target}
          tabIndex={tabIndex}
          role={linkRole}
          aria-selected={linkRole === 'option' ? !!selected : undefined}
          aria-current={selected ? 'true' : undefined}
          className="vds-dropdown-item__link">
          <span
            className={classNames(
              'vds-dropdown-item__label',
              isTruncated && 'vds-dropdown-item__label--truncated',
            )}>
            {label}
          </span>
        </LinkWrapper>
      ) : (
        <span
          className={classNames(
            'vds-dropdown-item__label',
            isTruncated && 'vds-dropdown-item__label--truncated',
          )}>
          {label}
        </span>
      )}
      {children}
      {chevron && (
        <Icon className="vds-dropdown-item__chevron" name="chevron-forward" size="small" />
      )}
    </Component>
  );
};
